import React, {useCallback, useEffect} from 'react'
import {observer} from 'mobx-react-lite'
import {BaseButton, Box, Button, Flex, Text} from '../../vanilla'
import {useBasketStore, useCustomerStore, useRapidDeliveryStore} from '../../store/hooks/useStore'
import {ExclamationCircleIcon} from '../icons'
import * as styles from './styles.css'
import {Link} from '../link'
import {useTimer} from '../../hooks/use-timer'
import useNavigation from '../../hooks/use-navigation'
import {useDeliveryReservationTime} from '../../hooks/use-delivery-reservation-time'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'
import { useAlcoholRestrictions } from '../../hooks/use-alcohol-restrictions'
import { Hit } from '../../store/ProductStore'

interface CountdownProps {
  slotExpiration: string
  textVariant?: 'text2' | 'text3' | 'text4'
  color?: 'accent0' | 'accent2'
  onCountdownEnd?: () => void
}

export const Countdown = ({
  slotExpiration,
  textVariant = 'text4',
  onCountdownEnd,
  color
}: CountdownProps) => {
  const { minutes, seconds, isFinished } = useTimer(slotExpiration)

  useEffect(() => {
    if (typeof onCountdownEnd === 'function' && isFinished) {
      onCountdownEnd()
    }
  }, [onCountdownEnd, isFinished])

  const displayMinutes = isFinished ? '0' : minutes
  const displaySeconds = isFinished ? '00' : seconds

  return (
    <Text
      as="span"
      minWidth="60px"
      display="inline-block"
      color={color}
      variant={textVariant}
    >
      {`${displayMinutes}m:${displaySeconds}s`}
    </Text>
  )
}

interface ReservationStatusProps {
  rebookSlot: () => void
}

export const ReservationExpiryMessage = () => {
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const deliveryReservationDuration = useDeliveryReservationTime()

  return (
    <Box width={['full', 'auto']}>
      {!isRapidDeliveryBooked && (
        <Flex align="center" justify={['flex-start', 'flex-end']} gap="4px">
          <ExclamationCircleIcon color="accent0" marginBottom="2px" />
          <Text color="accent0" variant="text4" data-test-selector="delivery-reservation-expired">
            Your delivery reservation has expired!
          </Text>
        </Flex>
      )}
      <Text variant="text4" textAlign={['left', 'right']}>
        {deliveryReservationDuration}
      </Text>
    </Box>
  )
}

export const ReservationStatusContainer = ({rebookSlot}: ReservationStatusProps) => {
  const navigate = useNavigation()
  const {isRegistered} = useCustomerStore()
  const {basket, currentDeliveryDay, setOpenMiniBasketUI} = useBasketStore()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const {isReservationExpired} = useSlotExpiration()
  const {isAvailable} = currentDeliveryDay
  const {checkIfAnyProductsAreAlcohol, isCurrentSlotAlcoholRestricted} = useAlcoholRestrictions()
  const isRestricted = checkIfAnyProductsAreAlcohol(basket?.productItems as Hit[]) && isCurrentSlotAlcoholRestricted

  const getBookButtonAction = () => {
    if (isAvailable && isRestricted) return { onClick: () => setOpenMiniBasketUI(true) }
    if (isAvailable) return { onClick: rebookSlot }
    return { as: Link, to: '/select-delivery' }
  }

  const getBookButtonLabel = () => {
    if (isAvailable && isRestricted) return 'Edit Basket'
    if (isAvailable || (isRapidDeliveryBooked && isReservationExpired())) return 'Rebook'
    return 'Change Delivery'
  }

  const renderBookingAction = useCallback(() => {
    if (!isRegistered && isAvailable) {
      return (
        <Button
          size="sm"
          width="full"
          variant="primary"
          onClick={() =>
            navigate('/login', 'push', {directedFrom: '/select-delivery'})
          }
        >
          Sign In to Rebook
        </Button>
      )
    }

    return (
      <BaseButton
        variant="primary"
        size="sm"
        width="full"
        style={{padding: '0 20px !important'}}
        {...getBookButtonAction()}
      >
        <Text variant="unstyled" fontSize="inherit" fontWeight="inherit">
          {getBookButtonLabel()}
        </Text>
      </BaseButton>
    )
  }, [isAvailable, isRegistered])

  return (
    <Flex
      align="center"
      gap={isReservationExpired() ? '20px' : '8px'}
      direction={isReservationExpired() ? ['column', 'row'] : 'column'}
      className={styles.reservationStatusContainer}
    >
      {isReservationExpired() ? (
        <>
          <Box
            display={['none', 'block']}
            borderBottom={['1px', '0px']}
            borderColor="gray900"
            paddingBottom={['20px', '0px']}
            flex="1"
          >
            <ReservationExpiryMessage />
          </Box>
          <Flex
            className={styles.reservationStatusButton}
            justify="center"
            align="center"
          >
            {renderBookingAction()}
          </Flex>
        </>
      ) : (
        <>
          <Flex
            align="center"
            justify="center"
            className={styles.reservationStatusButton}
          >
            <BaseButton
              as={Link}
              href="/select-delivery"
              variant="outlinePrimary"
              maxHeight="36px"
              data-test-selector="change-delivery-date-button"
              width={['full', 'auto']}
            >
              <Text variant="unstyled" fontSize="inherit" fontWeight="inherit" paddingX="24px">
                Change Delivery
              </Text>
            </BaseButton>
          </Flex>
        </>
      )}
    </Flex>
  )
}

export const ReservationStatus = observer(ReservationStatusContainer)
